import { 
  createContext,
  useContext,
  useState,
  useEffect
} from 'react';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession } from 'aws-amplify/auth';
import jwtDecode from 'jwt-decode';
import { DateTime } from 'luxon';
import FeedbackContext from './FeedbackContext';
import StoreContext from './StoreContext';
import { useLocation } from 'react-router-dom';
import { getCurrentStoreId, parseQueryStrings } from '../helpers/QueryParams';
import { useBebackApi } from '../hooks/useBebackApi';
import { validateSession } from '../services/authService';
import Proptypes from 'prop-types';

const { REACT_APP_MR_STAGE }= process.env;

const UserContext = createContext();

const UserProvider = ({ children }) => {
  const { isOktaUserFeaturesAdmin } = useBebackApi()
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFeaturesAdmin, setIsFeaturesAdmin] = useState(false);
  const [username, setUsername] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const { resetStore, fetchStores, updateStore, storeList } = useContext(StoreContext);
  const { setIsLoading } = useContext(FeedbackContext);
  const { search } = useLocation();

  useEffect(() => {
    const queryParams = search && parseQueryStrings(search);
    const currentStoreId = queryParams.length > 0 && getCurrentStoreId(queryParams)
    async function setLocalStoreId() {
      if (currentStoreId) await localStorage.setItem('currentStoreId', currentStoreId);
    }
    setLocalStoreId();
    //Okta/cognito auth events
    Hub.listen('auth', async ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          setIsLoggedIn(true);
          break;
        case 'cognitoHostedUI':
          break;
        case 'signOut':
          setIsLoggedIn(false);
          break;
        case 'tokenRefresh':
          break;
        default:
          setIsLoggedIn(false);
      }

    });
    //inStore custom auth events
    Hub.listen('storeAuth', async ({ payload: { event, data } }) => {
      console.log("STORE-AUTH: ", event);
      setIsFeaturesAdmin(false);

      switch (event) {
        case 'signIn':
          const userInfo = jwtDecode(data);
          const storeNo = ['qa', 'preprod'].includes(REACT_APP_MR_STAGE) ? '001' : userInfo.data.storeId // MR-671
          await localStorage.setItem('sessionStoreNo', storeNo);
          await localStorage.setItem('isInStore', true);
          await localStorage.setItem('authTime', DateTime.fromObject(userInfo.iat).toISO());
          await localStorage.setItem('token', data);
          
          if (storeNo === "Dev" || parseInt(storeNo) === 1){
            setIsAdmin(true)
            await fetchStores(true);
            if(currentStoreId){
              await updateStore(currentStoreId, true);
            } else {
              resetStore()
            }
          } else {
            console.log('settingStore', storeNo)
            await updateStore(parseInt(storeNo), false )
          }
          setIsLoggedIn(true);
          break;
        case 'expired':
          localStorage.clear();
          await alert('Your session has expired, press Ok to sign in.');
          setIsLoggedIn(false);
          break;
        case 'signOut':
          localStorage.clear();
          setIsLoggedIn(false);
          break;
        default:
          setIsLoggedIn(false);
      }
    });
    initiateAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentStoreId =  localStorage.getItem('currentStoreId')
    if(storeList.length > 0 && currentStoreId){
      updateStore(currentStoreId, true);
      localStorage.removeItem('currentStoreId')
    }
  }, [storeList.length])

  const initiateAuth = async () => {
    try {
      const isValidated = await validateSession();
      const isInStore = await localStorage.getItem('isInStore');
      const sessionStoreNo = await localStorage.getItem('sessionStoreNo');
      const queryParams = search && parseQueryStrings(search);
      const currentStoreId = queryParams.length > 0 && getCurrentStoreId(queryParams) ? getCurrentStoreId(queryParams) : localStorage.getItem('currentStoreId');
      if (isValidated && isInStore) {
        setIsLoggedIn(true);
        if (sessionStoreNo) {
          if (sessionStoreNo === "Dev" || parseInt(sessionStoreNo) === 1) {
            setIsAdmin(true);
            await fetchStores(true);
            if(currentStoreId){
              localStorage.setItem('currentStoreId', currentStoreId);
            }
          } else {
            console.log('settingStore', sessionStoreNo);
            if (!isNaN(parseInt(sessionStoreNo))) {
              await updateStore(parseInt(sessionStoreNo), false );
            }
          }
          setUsername(`in-store-auth-${sessionStoreNo}`);
        }
      } else if (isValidated) {
        const cognitoTokens = (await fetchAuthSession()).tokens;
        const sesh = cognitoTokens?.idToken
        const { locale, identities } = sesh.payload;
        const defaultStore = parseInt(locale);
        localStorage.setItem('defaultStore', defaultStore);
        setUsername(identities[0].userId);

        setIsLoggedIn(true); //Minimize the time the "Login" red button is shown

        if (defaultStore === 1) {

          setIsAdmin(true);
          await fetchStores(true);
          setIsFeaturesAdmin(await isOktaUserFeaturesAdmin(identities[0].userId))

          if(currentStoreId){
            localStorage.setItem('currentStoreId', currentStoreId);
          }

        } else {
          setIsAdmin(false)
          setIsFeaturesAdmin(false)
          await updateStore(defaultStore, false);
        }

      } else {
        setIsLoggedIn(false);
      }
    } catch (error) {
      console.log(`auth error: ${error}`);
      setIsLoggedIn(false);
    }
    finally {
      setIsLoading(false);
      setIsAuthLoading(false);
    }
  };

  const contextValue = {
    isAdmin,
    isFeaturesAdmin,
    username,
    isLoggedIn,
    isAuthLoading,
  }

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );

}

UserProvider.propTypes = {
  children: Proptypes.element
}

export { UserContext as default, UserProvider };